/**
 * Solicitudes de IA (SIA).
 *
 * El eslabón antes del inventario: alguien pide una IA nueva, uno de los
 * cinco la evalúa, y sólo si se aprueba entra al FDS-043.
 *
 * OJO CON LAS DOS ÁREAS. Son catálogos distintos y sus ids NO coinciden:
 *   idArea    → adm_area, la que el solicitante conoce.
 *   idCatArea → iso42001_cat_area, la de la norma, que escoge quien
 *               evalúa al aprobar. Es la única que viaja al inventario.
 *
 * Y urlIa NO es la "ubicación" del inventario: allá ubicación significa
 * dónde corre el activo (proveedor, On-Prem, Cloud), no su dirección web.
 */

export type EstatusSolicitudIA =
  | "SOLICITADA"
  | "EN_EVALUACION"
  | "INFORMACION_REQUERIDA"
  | "APROBADA"
  | "RECHAZADA"
  | "CANCELADA";

export interface SolicitudIA {
  idSolicitudIa?: number;
  /** SIA-0001. Lo arma el servidor a partir del id; no es una columna. */
  folio?: string;
  estatus?: EstatusSolicitudIA;

  idUsuarioSolicita?: number;
  solicitante?: string;
  idArea?: number;
  area?: string;
  telefonoContacto?: string;

  nombreIa?: string;
  urlIa?: string;
  /** El objetivo de la solicitud. */
  proposito?: string;
  /** Para qué se usará. */
  usoPrevisto?: string;
  proveedor?: string;
  /** 'SI' o 'NO': si va a tratar datos de la empresa o personales. */
  datosSensibles?: string;
  fechaRequerida?: string;

  fechaSolicitud?: string;
  fechaUltimaAfectacion?: string;

  idUsuarioResponsable?: number;
  responsable?: string;
  fechaAsignacion?: string;

  fechaDictamen?: string;
  idUsuarioDictamen?: number;
  dictaminador?: string;
  dictamenMotivo?: string;
  idCatArea?: number;
  catArea?: string;

  fechaCancelacion?: string;
  idUsuarioCancela?: number;

  /** Lleno cuando la solicitud aprobada ya entró al inventario. */
  idInventarioIa?: number;
  inventarioFolio?: string;
  /** Y el activo que nació con ella en la Matriz de Riesgos IA (ACT-006). */
  idMatrizActivo?: number;
  matrizFolio?: string;
  /**
   * Si su alta en la Matriz de Riesgos IA quedó PROPUESTA (con el control de
   * cambios de la matriz encendido): PENDIENTE | APLICADO | DESCARTADO.
   * Mientras esté PENDIENTE todavía no está en la matriz y no hay matrizFolio.
   */
  matrizPropuesta?: string;

  /** Cuántos archivos lleva (los que subió quien la pidió: el DAR y su apoyo). */
  archivos?: number;

  /**
   * Cuántos archivos de EVIDENCIA DE LA EVALUACIÓN lleva: los que sube quien
   * la atiende para sustentar el dictamen (origen SOLICITUD_IA_EVAL). Los ve
   * también quien la pidió.
   */
  evidenciasEval?: number;
}

/** Un renglón de la historia: bitácora y comentarios en la misma línea. */
export interface SolicitudIAHistorial {
  idHistorial?: number;
  idSolicitudIa?: number;
  /** ALTA | COMENTARIO | ASIGNACION | CAMBIO_ESTATUS | ALTA_INVENTARIO | ALTA_MATRIZ | PROPUESTA_MATRIZ | ARCHIVO | EVIDENCIA */
  accion?: string;
  estatusAnterior?: string;
  estatusNuevo?: string;
  comentario?: string;
  idUsuario?: number;
  usuario?: string;
  fecha?: string;
}

export interface SolicitudIAReq {
  solicitud?: SolicitudIA;
  dar?: SolicitudIADar;
  idSolicitudIa?: number;
  estatus?: string;
  comentario?: string;
  idUsuarioResponsable?: number | null;
  idCatArea?: number | null;
  busca?: string;
  filtroEstatus?: string;
  filtroArea?: number | null;
  filtroResponsable?: number | null;
  incluyeCanceladas?: boolean;
  pagina?: number;
  porPagina?: number;
}

/* ===================== El DAR (FOR-012) =====================
   El documento con el que se justifica una IA. Quien lo tiene en Excel
   lo sube; quien no, lo captura en pantalla y queda exactamente lo
   mismo, campo por campo. */

export interface DarInvolucrado {
  nombre?: string;
  area?: string;
}

export interface DarCriterio {
  criterio?: string;
  peso?: number;
  /** Una calificación por alternativa, en el mismo orden que `alternativas`. */
  calif?: number[];
}

export interface DarCambio {
  version?: string;
  descripcion?: string;
  elaboro?: string;
  autorizo?: string;
  fecha?: string;
}

export interface SolicitudIADar {
  idDar?: number;
  idSolicitudIa?: number;

  proyectoArea?: string;
  situacionActual?: string;
  alternativasTexto?: string;
  solucionFinal?: string;
  riesgos?: string;

  tiempoImplementacion?: string;
  costosImplementacion?: string;
  recursoHumanoMaterial?: string;
  fechaImplementacion?: string;
  responsableImplementacion?: string;

  /**
   * Estas cuatro viajan como TEXTO JSON: así están en la base, y así las
   * manda y las recibe el servidor, que no las interpreta. El modal las
   * convierte a objeto al abrir y de vuelta a texto al guardar.
   */
  involucrados?: string;
  alternativas?: string;
  criterios?: string;
  historialCambios?: string;

  idUsuario?: number;
  fechaCreacion?: string;
  fechaModificacion?: string;
}

/** Los ocho criterios del FOR-012, con el peso que trae el formato. */
export const CRITERIOS_DAR: DarCriterio[] = [
  { criterio: "Costo total", peso: 5 },
  { criterio: "Nivel de servicio", peso: 4 },
  { criterio: "Soporte técnico", peso: 3 },
  { criterio: "Funcionalidad", peso: 4 },
  { criterio: "Seguridad de la información", peso: 5 },
  { criterio: "Disponibilidad", peso: 4 },
  { criterio: "Rendimiento", peso: 5 },
  { criterio: "Dependencia del proveedor", peso: 5 },
];

/**
 * La escala del FOR-012, tal cual la dice el formato.
 *
 * `nombre` va aparte de `texto` porque la leyenda del modal lo pinta en
 * negrita y el número al lado, como la tablita del Excel; `texto` es la
 * línea corrida, que sirve para un tooltip o un `title`.
 */
export const ESCALA_DAR = [
  { valor: 1, nombre: "No Aceptable", texto: "1 · No aceptable" },
  { valor: 2, nombre: "Poco Aceptable", texto: "2 · Poco aceptable" },
  { valor: 3, nombre: "Regular", texto: "3 · Regular" },
  { valor: 4, nombre: "Aceptable", texto: "4 · Aceptable" },
  { valor: 5, nombre: "Excelente", texto: "5 · Excelente" },
];

export interface SolicitudIARes {
  solicitudes?: SolicitudIA[];
  solicitud?: SolicitudIA;
  historial?: SolicitudIAHistorial[];
  totalItems?: number;
  /** Si quien pregunta es de los cinco que atienden. */
  puedeAtender?: boolean;
  responsables?: SolicitudIA[];
  idGenerado?: number;
  /** El activo recién creado en el inventario, al aprobar. */
  idInventarioGenerado?: number;
  /** Y el que nació con él en la Matriz de Riesgos IA, al aprobar. */
  idMatrizActivoGenerado?: number;
  matrizFolioGenerado?: string;
  /** "PENDIENTE" si, al aprobar, a la matriz entró como propuesta y no hay ACT todavía. */
  matrizPropuestaGenerada?: string;
  /** El DAR de la solicitud, si se capturó en pantalla. */
  dar?: SolicitudIADar;
  /** El nombre de quien está dentro. El formulario lo muestra. */
  yo?: string;
  codigoError?: string;
  mensajeError?: string;
}

/**
 * Cómo se lee cada estatus en pantalla, con qué color y con qué icono.
 *
 * El color y el icono son los MISMOS que los de la columna donde cae ese
 * estatus en el tablero: al abrir una tarjeta azul, el detalle tiene que
 * seguir siendo azul o parece que se abrió otra cosa.
 */
export const ESTATUS_SIA: Array<{
  valor: EstatusSolicitudIA;
  texto: string;
  clase: string;
  icono: string;
  color: string;
}> = [
  { valor: "SOLICITADA", texto: "Solicitada", clase: "es-solicitada",
    icono: "inbox", color: "#2563eb" },
  { valor: "EN_EVALUACION", texto: "En evaluación", clase: "es-evaluacion",
    icono: "manage_search", color: "#b45309" },
  { valor: "INFORMACION_REQUERIDA", texto: "Falta información", clase: "es-info",
    icono: "mark_email_read", color: "#c2410c" },
  { valor: "APROBADA", texto: "Aprobada", clase: "es-aprobada",
    icono: "verified", color: "#15803d" },
  { valor: "RECHAZADA", texto: "Rechazada", clase: "es-rechazada",
    icono: "block", color: "#b91c1c" },
  { valor: "CANCELADA", texto: "Retirada", clase: "es-cancelada",
    icono: "undo", color: "#64748b" },
];

/** El origen con el que los archivos viven en el almacén del módulo. */
export const ORIGEN_SOLICITUD_IA = "SOLICITUD_IA";

/**
 * La evidencia de la evaluación: lo que sube quien atiende la solicitud
 * para sustentar su dictamen. Origen aparte en el mismo almacén, para no
 * mezclarse con el DAR de quien pidió. Máximo 20 caracteres (tipo_origen
 * es VARCHAR(20)).
 */
export const ORIGEN_SOLICITUD_IA_EVAL = "SOLICITUD_IA_EVAL";

/**
 * Las cinco columnas del tablero, con el vocabulario de tickets que ya
 * conoce la gente de Solser.
 *
 * Los estatus internos NO se renombran: la base y las reglas del
 * servidor siguen hablando de SOLICITADA / EN_EVALUACION / etc., que es
 * lo que significan de verdad. Aquí sólo se dice en qué columna cae cada
 * uno, y así el tablero se puede reetiquetar sin migrar datos.
 *
 *   Abiertas   nadie la ha tomado todavía
 *   Proceso    alguien la está evaluando
 *   Atendidas  ya hubo respuesta: la pelota está con quien la pidió
 *   Vo.Bo.     autorizada — el visto bueno
 *   Cerradas   no autorizadas y retiradas
 */
export const COLUMNAS_SIA: Array<{
  titulo: string;
  icono: string;
  color: string;
  /** Qué significa estar en esta etapa, en cuatro palabras. */
  pista: string;
  /** Qué se dice cuando no hay nada: "Nada aquí" no explica nada. */
  vacia: string;
  estatus: EstatusSolicitudIA[];
}> = [
  { titulo: "Abiertas", icono: "inbox", color: "#2563eb",
    pista: "Nadie la ha tomado", vacia: "Nada por tomar", estatus: ["SOLICITADA"] },
  { titulo: "Proceso", icono: "manage_search", color: "#b45309",
    pista: "Alguien la está evaluando", vacia: "Nada en proceso", estatus: ["EN_EVALUACION"] },
  { titulo: "Atendidas", icono: "mark_email_read", color: "#c2410c",
    pista: "Esperan respuesta de quien pidió", vacia: "Nada esperando respuesta", estatus: ["INFORMACION_REQUERIDA"] },
  { titulo: "Vo.Bo.", icono: "verified", color: "#15803d",
    pista: "Autorizadas, ya en el inventario", vacia: "Ninguna autorizada", estatus: ["APROBADA"] },
  { titulo: "Cerradas", icono: "task_alt", color: "#64748b",
    pista: "No autorizadas o retiradas", vacia: "Ninguna cerrada", estatus: ["RECHAZADA", "CANCELADA"] },
];
